import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useGetLmsCreateRequestByIdQuery, useGetLmsVehicleTrainingMatchesQuery, useGetLmsLookupTranslationsQuery, useGetUserByDealerDropdownQuery } from '../../../../api';
import LmsCodeDropdown from '../../components/LmsCodeDropdown';
import useLmsCodeOptions from '../../hooks/useLmsCodeOptions';
import useLmsLanguageOptions from '../../hooks/useLmsLanguageOptions';
import UserDropdown from '../../../../components/common/UserDropdown';
import ExpandableNameList from './ExpandableNameList';
import TechnicianUserDropdown from './TechnicianUserDropdown';
import useEndDatePreview from '../hooks/useEndDatePreview';

// "Talep Edilen Eğitimler" akışı için sabit — bkz. src/api/endpoints/lmsCreateRequest.js üstündeki not.
const REQUEST_TYPE = 2;

// Calendar alanında saat seçimi yok (Excel spesifikasyonu sadece "Takvim" diyor); backend tam
// datetime bekliyor (docs/lms-create-request.md örnekleri "T09:00:00" kullanıyor), o yüzden
// seçilen güne sabit bir saat ekleniyor. Sadece GÜN karşılaştırması yapıldığı belgeleniyor
// ("bugünden önceki bir gün olamaz"), saatin kendisi iş kuralı taşımıyor.
const toRequestDateTime = (date) => {
    if (!date) return null;
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return null;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T09:00:00`;
};

const formatDisplayDate = (date) => (date ? date.toLocaleDateString('tr-TR') : '');

const startOfToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
};

let rowKeySeq = 0;
const nextRowKey = () => `row-${++rowKeySeq}`;
const emptyTechnicianRow = () => ({ rowKey: nextRowKey(), userId: null, duty: null });

const emptyFormData = () => ({
    id: null,
    vehicleModel: null,
    trainingType: null,
    numberOfParticipants: null,
    participants: [],
    numberOfTechnician: null,
    technicians: [],
    trainingDayCount: null,
    preferTrainingDate: null,
    alternativeDate: null,
    trainingEndDate: null,
    alternativeEndDate: null,
    location: null,
    trainingLanguage: '',
    description: '',
    commitmentApproval: 0,
    status: 0,
    isActive: 1
});

const TrainingRequestForm = ({ visible, onHide, onSubmit, initialData, loading, handleDelete, readOnly = false }) => {
    const { th } = useLanguage();
    const toast = useRef(null);
    const isEdit = !!(initialData && (initialData.requestId || initialData.id));
    const editId = isEdit ? initialData.requestId ?? initialData.id : null;
    const { data: detailData, isLoading: isLoadingDetail } = useGetLmsCreateRequestByIdQuery(editId, { skip: !isEdit || !visible });

    const sourceData = isEdit ? detailData || initialData : null;

    const { languageOptions, isLoadingLanguages } = useLmsLanguageOptions();

    // Özet ekranında kod yerine okunabilir metin göstermek için (LmsCodeDropdown aynı sorguları
    // kendi içinde yapıyor ama seçili etiketi dışarı vermiyor).
    const { data: vehicleModelsData } = useLmsCodeOptions('VEHICLE_MODEL');
    const { data: trainingTypesData } = useLmsCodeOptions('TRAINING_TYPES');
    const { data: trainingLocationsData } = useLmsCodeOptions('TRAINING_LOCATION');

    const { data: technicianDutiesData, isLoading: isLoadingDuties } = useGetLmsLookupTranslationsQuery({ groupCode: 'TECHNICIAN_DUTY', isActive: 1, pageNumber: 1, pageSize: 100 });
    const dutyOptions = useMemo(() => (technicianDutiesData?.items || []).map((item) => ({ value: item.code, label: item.text })), [technicianDutiesData]);

    // Özet ekranında teknisyen adını göstermek için (TechnicianUserDropdown aynı sorguyu kendi
    // içinde yapıyor ama seçili kullanıcının adını dışarı vermiyor).
    const { data: dealerUsersResponse } = useGetUserByDealerDropdownQuery(undefined, { skip: !visible });
    const userNameById = useMemo(() => {
        const map = new Map();
        (dealerUsersResponse?.data || []).forEach((user) => map.set(user.id, user.name));
        return map;
    }, [dealerUsersResponse]);

    const [formData, setFormData] = useState(emptyFormData());

    // "Talep Edilen Eğitimler" akışında Eğitim Türü artık Araç Modeli'ne göre DARALTILIYOR 
    const { data: vehicleTrainingMatches, isFetching: isLoadingTrainingTypes } = useGetLmsVehicleTrainingMatchesQuery({ vehicleCode: formData.vehicleModel }, { skip: !visible || !formData.vehicleModel });
    const trainingTypeOptions = useMemo(() => (vehicleTrainingMatches || []).map((item) => ({ value: item.trainingCode, label: item.trainingName })), [vehicleTrainingMatches]);
    const [localSubmitted, setLocalSubmitted] = useState(false);

    useEffect(() => {
        if (!visible) return;
        if (isEdit && !sourceData) return; // detay yüklenene kadar bekle

        if (!sourceData) {
            setFormData(emptyFormData());
            setLocalSubmitted(false);
            return;
        }

        setFormData({
            id: sourceData.requestId ?? sourceData.id ?? null,
            vehicleModel: sourceData.vehicleModel ?? null,
            trainingType: sourceData.trainingType ?? null,
            numberOfParticipants: sourceData.numberOfParticipants ?? null,
            participants: (sourceData.participants || []).map((p) => ({ id: p.participantId, name: p.participantFullName, displayValue: `${p.participantId} - ${p.participantFullName}` })),
            numberOfTechnician: sourceData.numberOfTechnician ?? null,
            technicians: (sourceData.technicians || []).map((t) => ({ rowKey: nextRowKey(), userId: t.userId ?? null, duty: t.duty ?? null })),
            preferTrainingDate: sourceData.preferTrainingDate ? new Date(sourceData.preferTrainingDate) : null,
            alternativeDate: sourceData.alternativeDate ? new Date(sourceData.alternativeDate) : null,
            trainingDayCount: sourceData.trainingDayCount ?? null,
            trainingEndDate: sourceData.trainingEndDate ? new Date(sourceData.trainingEndDate) : null,
            alternativeEndDate: sourceData.alternativeEndDate ? new Date(sourceData.alternativeEndDate) : null,
            location: sourceData.locations?.[0]?.locationCode ?? null,
            trainingLanguage: sourceData.trainingLanguage ?? '',
            description: sourceData.description ?? '',
            commitmentApproval: sourceData.commitmentApproval ?? 0,
            status: sourceData.status ?? 0,
            isActive: sourceData.isActive ?? 1
        });
        setLocalSubmitted(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, isEdit, sourceData]);

    // Katılımcı Sayısı alanı artık elle girilmiyor; Katılımcılar alanında seçilen kişi sayısından
    // otomatik hesaplanıyor (kullanıcı isteği) — bkz. aşağıdaki InputNumber'ın disabled olması.
    useEffect(() => {
        setFormData((prev) => (prev.participants.length === (prev.numberOfParticipants || 0) ? prev : { ...prev, numberOfParticipants: prev.participants.length || null }));
    }, [formData.participants]);

    // Teknisyen Sayısı da aynı şekilde Teknisyenler alanındaki satır sayısından otomatik hesaplanır.
    useEffect(() => {
        setFormData((prev) => (prev.technicians.length === (prev.numberOfTechnician || 0) ? prev : { ...prev, numberOfTechnician: prev.technicians.length || null }));
    }, [formData.technicians]);

    const isCalculatingEndDate = useEndDatePreview({
        enabled: visible && !isLoadingDetail,
        preferTrainingDate: toRequestDateTime(formData.preferTrainingDate),
        alternativeDate: toRequestDateTime(formData.alternativeDate),
        trainingDayCount: formData.trainingDayCount,
        setFormData
    });

    const updateTechnicianRow = (rowKey, patch) => setFormData((prev) => ({ ...prev, technicians: prev.technicians.map((row) => (row.rowKey === rowKey ? { ...row, ...patch } : row)) }));
    const addTechnicianRow = () => setFormData((prev) => ({ ...prev, technicians: [...prev.technicians, emptyTechnicianRow()] }));
    const removeTechnicianRow = (rowKey) => setFormData((prev) => ({ ...prev, technicians: prev.technicians.filter((row) => row.rowKey !== rowKey) }));

    // NOT: React state güncellemeleri (setLocalSubmitted) senkron değildir — handleSubmit içinde
    // setLocalSubmitted(true) çağrıldıktan hemen sonra eski isFieldInvalid çağrılırsa hâlâ eski
    // `localSubmitted=false` değeri okunur ve ilk "Kaydet" tıklamasında hiçbir alan doğrulanmadan
    // API isteği atılabilir. Bu yüzden asıl geçerlilik kararı `localSubmitted`'dan bağımsız, saf
    // bir fonksiyonla (checkFieldInvalid) veriliyor; isFieldInvalid sadece kırmızı çerçeve gösterimi
    // için (yalnızca bir kez "Kaydet"e basıldıktan sonra) kullanılıyor.
    const checkFieldInvalid = (field) => {
        if (field === 'numberOfParticipants') return !(Number(formData.numberOfParticipants) > 0);
        if (field === 'participants') return !(formData.participants?.length > 0);
        if (field === 'preferTrainingDate') return !formData.preferTrainingDate || formData.preferTrainingDate < startOfToday();
        if (field === 'alternativeDate') return !!formData.alternativeDate && formData.alternativeDate < startOfToday();
        if (field === 'commitmentApproval') return Number(formData.commitmentApproval) !== 1;
        if (typeof formData[field] === 'string') return !formData[field].trim();
        return formData[field] == null;
    };

    const isFieldInvalid = (field) => {
        if (!localSubmitted) return false;
        return checkFieldInvalid(field);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setLocalSubmitted(true);

        const requiredFields = ['vehicleModel', 'trainingType', 'trainingLanguage', 'location'];
        const missingRequired = requiredFields.some((field) => checkFieldInvalid(field));
        const otherInvalid = checkFieldInvalid('numberOfParticipants') || checkFieldInvalid('participants') || checkFieldInvalid('preferTrainingDate') || checkFieldInvalid('alternativeDate') || checkFieldInvalid('commitmentApproval');

        if (missingRequired || otherInvalid) {
            toast.current?.show({ severity: 'error', summary: th('trainingRequest.missingField'), detail: th('trainingRequest.formValidationMessage'), life: 4000 });
            return;
        }

        const nonEmptyTechnicians = formData.technicians.filter((row) => row.userId || row.duty);

        const payload = {
            requestType: REQUEST_TYPE,
            vehicleModel: formData.vehicleModel,
            trainingType: formData.trainingType,
            numberOfParticipants: Number(formData.numberOfParticipants),
            preferTrainingDate: toRequestDateTime(formData.preferTrainingDate),
            alternativeDate: toRequestDateTime(formData.alternativeDate),
            trainingDayCount: formData.trainingDayCount != null && formData.trainingDayCount !== '' ? Number(formData.trainingDayCount) : null,
            trainingLanguage: formData.trainingLanguage.trim(),
            description: (formData.description || '').trim(),
            commitmentApproval: Number(formData.commitmentApproval),
            status: Number(formData.status),
            isActive: Number(formData.isActive),
            locationCodes: formData.location ? [formData.location] : [],
            preconditionCodes: [], // Ön koşul alanı formdan kaldırıldı (kullanıcı isteği)
            participantIds: formData.participants.map((p) => p.id),
            numberOfTechnician: nonEmptyTechnicians.length || null,
            technicians: nonEmptyTechnicians.map((row) => ({ userId: row.userId, duty: row.duty }))
        };

        if (isEdit) {
            payload.id = formData.id;
        }

        onSubmit(payload);
    };

    const textByCode = (list, code) => (list?.items || []).find((item) => item.code === code)?.text || code || '';
    const summaryVehicleModel = textByCode(vehicleModelsData, formData.vehicleModel);
    const summaryTrainingType = textByCode(trainingTypesData, formData.trainingType);
    const summaryLocation = textByCode(trainingLocationsData, formData.location);
    const participantNames = formData.participants.map((p) => p.name);
    const dutyLabelByCode = (code) => dutyOptions.find((opt) => opt.value === code)?.label || code || '';
    const technicianNames = formData.technicians
        .filter((row) => row.userId)
        .map((row) => `${userNameById.get(row.userId) || row.userId}${row.duty ? ' (' + dutyLabelByCode(row.duty) + ')' : ''}`);

    const renderFooter = () => (
        <div>
            {isEdit && handleDelete && <Button label={th('common.delete')} icon="pi pi-trash" severity="danger" type="button" onClick={() => handleDelete(formData.id)} disabled={loading} />}
            <Button label={th('common.cancel')} icon="pi pi-times" onClick={onHide} outlined type="button" />
            {!readOnly && <Button label={th('common.save')} icon="pi pi-check" onClick={handleSubmit} loading={!!loading} />}
        </div>
    );

    const isFormReady = !isEdit || !!sourceData;

    return (
        <Dialog
            visible={visible}
            footer={renderFooter()}
            style={{ width: '65vw', maxWidth: '900px' }}
            header={isEdit ? th('trainingRequest.editTitle') : th('trainingRequest.addTitle')}
            modal
            className="p-fluid"
            onHide={onHide}
            key={formData.id ?? 'new'}
        >
            <Toast ref={toast} />
            {!isFormReady || isLoadingDetail ? (
                <div className="flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="p-fluid">
                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label htmlFor="vehicleModel">
                                    {th('trainingRequest.fields.vehicleModel')} <span className="text-red-500">*</span>
                                </label>
                                <LmsCodeDropdown
                                    id="vehicleModel"
                                    groupCode="VEHICLE_MODEL"
                                    value={formData.vehicleModel}
                                    onChange={(value) => setFormData((prev) => (value === prev.vehicleModel ? prev : { ...prev, vehicleModel: value, trainingType: null }))}
                                    placeholder={th('trainingRequest.fields.selectPlaceholder')}
                                    invalid={isFieldInvalid('vehicleModel')}
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label htmlFor="trainingType">
                                    {th('trainingRequest.fields.trainingType')} <span className="text-red-500">*</span>
                                </label>
                                <Dropdown
                                    id="trainingType"
                                    value={formData.trainingType || null}
                                    options={trainingTypeOptions}
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={(e) => setFormData((prev) => ({ ...prev, trainingType: e.value }))}
                                    placeholder={formData.vehicleModel ? th('trainingRequest.fields.selectPlaceholder') : th('trainingRequest.fields.selectVehicleModelFirstPlaceholder')}
                                    disabled={!formData.vehicleModel}
                                    loading={isLoadingTrainingTypes}
                                    filter
                                    showClear
                                    className={isFieldInvalid('trainingType') ? 'p-invalid' : ''}
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-8">
                            <div className="field">
                                <label htmlFor="participants">
                                    {th('trainingRequest.fields.participants')} <span className="text-red-500">*</span>
                                </label>
                                {/* Bayi rolündeki kullanıcı için yalnızca kendi bayisindeki personeli listeler
                                    (Admin/WarrantyManager için tüm kullanıcılar) — bkz.
                                    "docs/LMS EĞİTİM TALEBİ_new-changed.txt" madde 4. */}
                                <UserDropdown
                                    value={formData.participants}
                                    onChange={(value) => setFormData((prev) => ({ ...prev, participants: value }))}
                                    placeholder={th('trainingRequest.fields.participantsPlaceholder')}
                                    className={isFieldInvalid('participants') ? 'p-invalid' : ''}
                                    dealerScoped
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-4">
                            <div className="field">
                                <label htmlFor="numberOfParticipants">
                                    {th('trainingRequest.fields.numberOfParticipants')} <span className="text-red-500">*</span>
                                </label>
                                {/* Katılımcı Sayısı artık Katılımcılar alanından seçilen kişi sayısından
                                    otomatik hesaplanıyor ve salt okunur (kullanıcı isteği). */}
                                <InputNumber id="numberOfParticipants" value={formData.numberOfParticipants} min={1} disabled className={isFieldInvalid('numberOfParticipants') ? 'p-invalid' : ''} />
                            </div>
                        </div>

                        <div className="col-12 md:col-4">
                            <div className="field">
                                <label htmlFor="numberOfTechnician">{th('trainingRequest.fields.numberOfTechnician')}</label>
                                {/* Teknisyen Sayısı, Teknisyenler alanındaki satır sayısından otomatik hesaplanıyor
                                    (Katılımcı Sayısı ile aynı desen). */}
                                <InputNumber id="numberOfTechnician" value={formData.numberOfTechnician} min={1} disabled />
                            </div>
                        </div>

                        <div className="col-12 md:col-4">
                            <div className="field">
                                <label htmlFor="trainingDayCount">{th('trainingRequest.fields.trainingDayCount')}</label>
                                <InputNumber id="trainingDayCount" value={formData.trainingDayCount} min={1} onValueChange={(e) => setFormData((prev) => ({ ...prev, trainingDayCount: e.value }))} />
                            </div>
                        </div>

                        <div className="col-12 md:col-4">
                            <div className="field">
                                <label htmlFor="preferTrainingDate">
                                    {th('trainingRequest.fields.preferTrainingDate')} <span className="text-red-500">*</span>
                                </label>
                                <Calendar
                                    id="preferTrainingDate"
                                    value={formData.preferTrainingDate}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, preferTrainingDate: e.value }))}
                                    dateFormat="dd.mm.yy"
                                    minDate={startOfToday()}
                                    showIcon
                                    className={isFieldInvalid('preferTrainingDate') ? 'p-invalid' : ''}
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-4">
                            <div className="field">
                                <label htmlFor="alternativeDate">{th('trainingRequest.fields.alternativeDate')}</label>
                                <Calendar
                                    id="alternativeDate"
                                    value={formData.alternativeDate}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, alternativeDate: e.value }))}
                                    dateFormat="dd.mm.yy"
                                    minDate={startOfToday()}
                                    showIcon
                                    showClear
                                    className={isFieldInvalid('alternativeDate') ? 'p-invalid' : ''}
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label>{th('trainingRequest.fields.trainingEndDate')}</label>
                                <InputText value={isCalculatingEndDate ? '...' : formatDisplayDate(formData.trainingEndDate)} disabled readOnly />
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label>{th('trainingRequest.fields.alternativeEndDate')}</label>
                                <InputText value={isCalculatingEndDate ? '...' : formatDisplayDate(formData.alternativeEndDate)} disabled readOnly />
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label htmlFor="location">
                                    {th('trainingRequest.fields.location')} <span className="text-red-500">*</span>
                                </label>
                                <LmsCodeDropdown
                                    id="location"
                                    groupCode="TRAINING_LOCATION"
                                    value={formData.location}
                                    onChange={(value) => setFormData((prev) => ({ ...prev, location: value }))}
                                    placeholder={th('trainingRequest.fields.selectPlaceholder')}
                                    invalid={isFieldInvalid('location')}
                                />
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label htmlFor="trainingLanguage">
                                    {th('trainingRequest.fields.trainingLanguage')} <span className="text-red-500">*</span>
                                </label>
                                <Dropdown
                                    id="trainingLanguage"
                                    value={formData.trainingLanguage || null}
                                    options={languageOptions}
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={(e) => setFormData((prev) => ({ ...prev, trainingLanguage: e.value }))}
                                    placeholder={th('trainingRequest.fields.selectPlaceholder')}
                                    loading={isLoadingLanguages}
                                    filter
                                    showClear
                                    className={isFieldInvalid('trainingLanguage') ? 'p-invalid' : ''}
                                />
                            </div>
                        </div>

                        <div className="col-12">
                            <div className="field">
                                <label htmlFor="description">{th('trainingRequest.fields.description')}</label>
                                <InputTextarea
                                    id="description"
                                    value={formData.description}
                                    rows={3}
                                    autoResize
                                    maxLength={1000}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                                    placeholder={th('trainingRequest.fields.descriptionPlaceholder')}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="surface-50 border-round p-3 mt-2">
                        <div className="flex align-items-center mb-2">
                            <h3 className="m-0" style={{ fontSize: '1rem' }}>
                                {th('trainingRequest.fields.technicians')}
                            </h3>
                            <Button type="button" label={th('trainingRequest.fields.addTechnician')} icon="pi pi-plus" text style={{ marginLeft: 'auto', flex: '0 0 auto', width: 'auto' }} onClick={addTechnicianRow} />
                        </div>
                        {formData.technicians.length === 0 && <div className="text-color-secondary text-sm">{th('trainingRequest.fields.noRows')}</div>}
                        {formData.technicians.map((row) => (
                            <div key={row.rowKey} className="surface-0 border-round p-3 mb-2 border-1 surface-border">
                                <div className="grid align-items-start">
                                    <div className="col-12 md:col-5">
                                        <label>{th('trainingRequest.fields.technician')}</label>
                                        <TechnicianUserDropdown value={row.userId} onChange={(value) => updateTechnicianRow(row.rowKey, { userId: value })} placeholder={th('trainingRequest.fields.technicianPlaceholder')} dealerScoped />
                                    </div>
                                    <div className="col-12 md:col-5">
                                        <label>{th('trainingRequest.fields.duty')}</label>
                                        <Dropdown
                                            value={row.duty}
                                            options={dutyOptions}
                                            optionLabel="label"
                                            optionValue="value"
                                            onChange={(e) => updateTechnicianRow(row.rowKey, { duty: e.value })}
                                            placeholder={th('trainingRequest.fields.selectPlaceholder')}
                                            loading={isLoadingDuties}
                                            filter
                                            showClear
                                        />
                                    </div>
                                    <div className="col-12 md:col-2 flex align-items-end justify-content-end" style={{ minHeight: '3.5rem' }}>
                                        <Button type="button" icon="pi pi-trash" severity="danger" text onClick={() => removeTechnicianRow(row.rowKey)} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="surface-100 border-round p-3 mt-2">
                        <h3 className="mt-0 mb-1" style={{ fontSize: '1rem' }}>
                            {th('trainingRequest.summary.title')}
                        </h3>
                        <p className="text-color-secondary mt-0 mb-3" style={{ fontSize: '0.85rem' }}>
                            {th('trainingRequest.summary.hint')}
                        </p>
                        <div className="grid text-sm">
                            <div className="col-12 md:col-6">
                                <strong>{th('trainingRequest.summary.vehicleModel')}:</strong> {summaryVehicleModel || th('trainingRequest.summary.none')}
                            </div>
                            <div className="col-12 md:col-6">
                                <strong>{th('trainingRequest.summary.trainingType')}:</strong> {summaryTrainingType || th('trainingRequest.summary.none')}
                            </div>
                            <div className="col-12 md:col-6">
                                <strong>{th('trainingRequest.summary.numberOfParticipants')}:</strong> {formData.numberOfParticipants ?? th('trainingRequest.summary.none')}
                            </div>
                            <div className="col-12 md:col-6">
                                <strong>{th('trainingRequest.summary.participants')}:</strong> <ExpandableNameList names={participantNames} emptyLabel={th('trainingRequest.summary.none')} />
                            </div>
                            <div className="col-12 md:col-6">
                                <strong>{th('trainingRequest.summary.numberOfTechnician')}:</strong> {formData.numberOfTechnician ?? th('trainingRequest.summary.none')}
                            </div>
                            <div className="col-12 md:col-6">
                                <strong>{th('trainingRequest.summary.technicians')}:</strong> <ExpandableNameList names={technicianNames} emptyLabel={th('trainingRequest.summary.none')} />
                            </div>
                            <div className="col-12 md:col-6">
                                <strong>{th('trainingRequest.summary.preferTrainingDate')}:</strong> {formData.preferTrainingDate ? formData.preferTrainingDate.toLocaleDateString('tr-TR') : th('trainingRequest.summary.none')}
                            </div>
                            <div className="col-12 md:col-6">
                                <strong>{th('trainingRequest.summary.alternativeDate')}:</strong> {formData.alternativeDate ? formData.alternativeDate.toLocaleDateString('tr-TR') : th('trainingRequest.summary.none')}
                            </div>
                            <div className="col-12 md:col-6">
                                <strong>{th('trainingRequest.summary.trainingDayCount')}:</strong> {formData.trainingDayCount ?? th('trainingRequest.summary.none')}
                            </div>
                            <div className="col-12 md:col-6">
                                <strong>{th('trainingRequest.summary.trainingEndDate')}:</strong> {formatDisplayDate(formData.trainingEndDate) || th('trainingRequest.summary.none')}
                            </div>
                            <div className="col-12 md:col-6">
                                <strong>{th('trainingRequest.summary.alternativeEndDate')}:</strong> {formatDisplayDate(formData.alternativeEndDate) || th('trainingRequest.summary.none')}
                            </div>
                            <div className="col-12 md:col-6">
                                <strong>{th('trainingRequest.summary.location')}:</strong> {summaryLocation || th('trainingRequest.summary.none')}
                            </div>
                            <div className="col-12 md:col-6">
                                <strong>{th('trainingRequest.summary.trainingLanguage')}:</strong> {formData.trainingLanguage || th('trainingRequest.summary.none')}
                            </div>
                            <div className="col-12">
                                <strong>{th('trainingRequest.summary.description')}:</strong> {formData.description || th('trainingRequest.summary.none')}
                            </div>
                        </div>
                    </div>

                    <div className="field-checkbox mt-3">
                        <Checkbox
                            inputId="commitmentApproval"
                            checked={Number(formData.commitmentApproval) === 1}
                            onChange={(e) => setFormData((prev) => ({ ...prev, commitmentApproval: e.checked ? 1 : 0 }))}
                            className={isFieldInvalid('commitmentApproval') ? 'p-invalid' : ''}
                        />
                        <label htmlFor="commitmentApproval" className="ml-2 cursor-pointer">
                            {th('trainingRequest.commitment.label')} <span className="text-red-500">*</span>
                        </label>
                    </div>
                </form>
            )}
        </Dialog>
    );
};

export default TrainingRequestForm;
